import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivateEvidenceImporterDocument,
  type ActivateEvidenceImporterMutation,
  type ActivateEvidenceImporterMutationVariables,
  DeactivateEvidenceImporterDocument,
  type DeactivateEvidenceImporterMutation,
  type DeactivateEvidenceImporterMutationVariables,
  GetEvidenceImporterDetailDocument,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type EvidenceImporter = {
  id: string;
  name: string;
};

type UseEvidenceImporterActivationOptions = {
  canExecute?: () => boolean;
};

export function useEvidenceImporterActivation({ canExecute }: UseEvidenceImporterActivationOptions = {}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const handleActionWithConfirm = useActionWithConfirm();

  const [deactivateEvidenceImporter] = useMutation<
    DeactivateEvidenceImporterMutation,
    DeactivateEvidenceImporterMutationVariables
  >(DeactivateEvidenceImporterDocument);

  const [activateEvidenceImporter] = useMutation<
    ActivateEvidenceImporterMutation,
    ActivateEvidenceImporterMutationVariables
  >(ActivateEvidenceImporterDocument);

  const onDeactivate = useCallback(
    (importer: EvidenceImporter) => {
      handleActionWithConfirm({
        confirmMessage: t("ui.deactivateX", { x: importer.name }),
        successMessage: t("feature.common.deactivatedSuccessfully", { model: "evidenceImporter" }),
        action: () =>
          deactivateEvidenceImporter({
            variables: { input: { id: importer.id } },
            refetchQueries: [{ query: GetEvidenceImporterDetailDocument, variables: { tenantId, id: importer.id } }],
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, deactivateEvidenceImporter, tenantId, canExecute],
  );

  const onActivate = useCallback(
    (importer: EvidenceImporter) => {
      handleActionWithConfirm({
        confirmMessage: t("ui.activateX", { x: importer.name }),
        successMessage: t("feature.common.activatedSuccessfully", { model: "evidenceImporter" }),
        action: () =>
          activateEvidenceImporter({
            variables: { input: { id: importer.id } },
            refetchQueries: [{ query: GetEvidenceImporterDetailDocument, variables: { tenantId, id: importer.id } }],
          }),
        canExecute,
      });
    },
    [handleActionWithConfirm, t, activateEvidenceImporter, tenantId, canExecute],
  );

  return {
    onActivate,
    onDeactivate,
  };
}
